import {
    BaseField,
    BaseZenputCollection,
    BaseZenputModel,
    FieldEventSearchCriterias,
    Images
} from '../../../../backbone-unorganized/shared/forms';
import { NA } from '../constants';

export const MobileBaseField = BaseField.extend(
    {
        is_currently_hidden: false, // allows us to know if a dependent field is currently being shown or hidden
        unsupported_field_type: false, // indicates that the field type is unrecognized by this app if true

        is_valid: null, // our validated:invalid/:valid listeners will set this true/false
        // so we don't have to keep asking isValid(true) - helps performance

        initialize: function (attrs, options) {
            options = options || {};
            $.extend(this, options);

            MobileBaseField.__super__.initialize.call(this, attrs, options);

            if ($f.flags.get_flag('fix_dependent_field_required_validation')) {
                // Templates ship dependent fields as `hidden: true, visible: true`; they only
                // become actually hidden once the form's field events run.
                // @see MobileForm._perform_form_field_events
                if (this.get('hidden')) {
                    this.is_currently_hidden = true;
                }
            }

            // bind all the events for showing and hiding fields based on the input of another.
            var this_ = this;

            $.each(this.get('events'), function (key, event) {
                this_.listenTo(this_, event.event + ':value', function () {
                    // Setting acceptance_value in these listeners as well
                    // so that the value is set before it gets to perform_event.
                    this_.set_acceptance_value();
                    this_.perform_event(event);
                });
            });

            // Make sure that empty values are always saved as '', as per model default
            this.listenTo(this, 'change', function (model) {
                var value = model.get('value');
                if (value instanceof Array && value.length == 0) {
                    model.set('value', '', { silent: true });
                }
                this_.set_acceptance_value();
            });

            // listen only if visibility based on location attributes is configured for this field
            if (this.has_dynamic_visibility()) {
                this.listenTo(
                    this.collection,
                    'dynamicform:location_attributes_fetched',
                    this.set_visibility_based_on_criteria
                );
            }
        },

        /**
         * Show or hide the field based on the configured attributes criteria for the given location
         * taking into account the location attributes and the visibility choice
         */
        set_visibility_based_on_criteria: function (location_attributes) {
            if (this.is_dynamic_visibility_allowed() === false) {
                return;
            }

            const show_sections = this.show.bind(this);
            const hide_sections = this.hide.bind(this);
            const choice = this.get_metadata_attribute('dynamic_visibility').visibility_choice;

            const criteria_actions = {
                success: choice === 'show' ? show_sections : hide_sections,
                fail: choice === 'show' ? hide_sections : show_sections
            };

            if (!location_attributes) {
                criteria_actions.fail();
                return;
            }

            const attribute_criteria_filter_set = new AttributeCriteriaFilterSet(
                this.get_metadata_attribute('dynamic_visibility').attribute_criteria_filters
            );

            const is_criteria_met = attribute_criteria_filter_set.evaluate(location_attributes);

            if (is_criteria_met) {
                criteria_actions.success();
            } else {
                criteria_actions.fail();
            }
        },

        // Hide this field - note that we unfortunately show via jquery dom manipulation here
        hide: function () {
            const field = $(`#field_${this.get('id')}`);
            if (field.hasClass('dependent_field')) {
                return;
            }
            this.set('visible', false, { silent: true });

            field.hide();
            this.is_currently_hidden = true;

            // Re-check validation when we hide a field. (We don't always re-validate when we submit, so
            // we need to do this here to make sure the hidden field does not prevent submission if invalid.)
            this.clear_values();
            this.trigger('field:hidden_field:hide', this);
        },

        // Show this field - note that we unfortunately show via jquery dom manipulation here
        show: function () {
            const field = $(`#field_${this.get('id')}`);
            if (field.hasClass('dependent_field')) {
                return;
            }
            this.set('visible', true, { silent: true });

            field.show();
            this.is_currently_hidden = false;

            // We need to trigger validation, but only if the error wizard is already open. Let the view handle it.
            this.trigger('field:hidden_field:show', this);
        },

        prepare_for_submission: function () {
            // optional: subclasses can implement to make final adjustments before form is submitted
        },

        // @author:david someday, we can use BaseZenputModel.restore() or bake this into BaseZenputModel:
        clear_values: function () {
            // don't clear if we don't need to so we don't trigger a collection change and
            // set date_modified on form load.
            if (
                this.get('value') === '' &&
                this.get('file') === null &&
                (typeof this.get('notes') === 'undefined' || this.get('notes') === null)
            ) {
                return;
            }

            this.set({ value: '', file: null, notes: '' });
            this.trigger('field:value_cleared'); // so any view listening can re-render
        },

        /**
         * Processes the action for a specified event from `this.get('events')`.
         * @param  {object} event The event object from `this.get('events')` to process - must contain `value` and `target` attributes.
         */
        perform_event: function (event) {
            var $target_field = $('#field_' + event.target);

            // @author:david somehow we're getting ghost field events, so that a parent field is trying to
            // show/hide a child field that doesn't exist. (See @bug:MOBILE-160.) Not sure how this is happening,
            // but we can catch it here:
            if (!$target_field.length) {
                return false;
            }

            var model = $f.mobile.get_window_collection().findWhere({ id: event.target });
            if (!model) {
                return;
            } // must be an old field (fixes @bug:MOBILE-160)

            var _hide = function () {
                model.is_currently_hidden = true;
                $target_field.hide();
                model.set('visible', false, { silent: true });

                // Re-check validation when we hide a field. (We don't always re-validate when we submit, so
                // we need to do this here to make sure the hidden field does not prevent submission if invalid.)
                model.clear_values();
                model.trigger('field:hidden_field:hide', model);
            };

            var _show = function () {
                model.is_currently_hidden = false;
                $target_field.show();
                model.set('visible', true, { silent: true });

                // We need to trigger validation, but only if the error wizard is already open. Let the view handle it.
                model.trigger('field:hidden_field:show', model);
            };

            var action = MobileBaseField.get_dependent_field_action_for_field_value_change(this, event);

            if (action === 'show') {
                return _show();
            }
            return _hide();
        },

        /**
         * Whether this field has to be answered as the form currently stands.
         *
         * Deliberately never consults the DOM: a field inside a collapsed section is not
         * `:visible` yet is still required, and treating it as optional let blank required
         * fields through the submit gate. @jira:ZP-18047
         *
         * Shared with MobileSectionField.get_stats() so the submit gate and the section
         * counters cannot disagree.
         */
        is_effectively_required: function () {
            if (this.is_currently_hidden) {
                return false;
            }

            if (this.get('not_compatible')) {
                return false;
            }

            // display-only fields have no answer to give
            if (_.contains(['section', 'instruction', 'instructions', 'formula'], this.get('type'))) {
                return false;
            }

            return !!this.get('required');
        },

        // Used by Backbone.Validate in subclasses to determine if required
        _is_required: function (val, attr, attrs) {
            if ($f.flags.get_flag('fix_dependent_field_required_validation')) {
                return this.is_effectively_required();
            }

            if (this.is_currently_hidden) {
                return false;
            }

            if (attrs.not_compatible) {
                return false;
            }

            // For dependent fields that are actually hidden, don't enforce their requirement:
            if (attrs.hidden && !$('#field_' + attrs.id).is(':visible')) {
                return false;
            }

            // If this is a section or instruction field ignore required value if set
            if (attrs.type === 'section' || attrs.type === 'instruction') {
                return false;
            }

            return attrs.required;
        },

        validation: {
            value: {
                required: function (value, attr, attrs) {
                    return this._is_required(value, attr, attrs);
                }
            }
        },

        /**
         * Determine if this field has a value. This is true if a) the field has a file (pre-upload), or b) the the
         * 'value' attribute has a value or is an array with at least one value.
         */
        has_value: function () {
            if ($.isArray(this.get('file')) ? this.get('file').length != 0 : !!this.get('file')) {
                return true;
            }

            if (!$.isArray(this.get('value')) && this.get('value') != null && this.get('value') !== '') {
                return true;
            }
            if ($.isArray(this.get('value')) && this.get('value').length > 0) {
                return true;
            }
            return false;
        },

        /**
         * Copy and modify data for autofilling from past submission
         */
        copy_from_past_submission_field: function (old_field) {
            var attributes_to_copy = old_field.get_attributes_for_autofill();
            if (!attributes_to_copy) {
                return;
            }

            this.set(attributes_to_copy, { silent: true });
            this.set_acceptance_value(true);
        },

        /**
         * Return the data that should be copied from this field into a new field for autofilling
         * as a past submission
         */
        get_attributes_for_autofill: function () {
            return { value: this.get('value') };
        }
    },
    {
        /**
         * Returns a string representing a field action for dependent fields depending on the value of the field and the
         * acceptance_values specified by the event passed in as an argument.
         *
         * This method supports both event acceptance values which can be multiple, as well as several legacy cases:
         *
         * - Yes/No fields with NA values default to hide.
         * - Event value of -1 signifies "Any" value and results in a show dependent field action.
         * - Event value matches with field value
         * - Legacy range logic.
         *
         * @param field
         * @param event
         * @returns {string} 'show' or 'hide'
         */
        get_dependent_field_action_for_field_value_change: function (field, event) {
            var field_val = field.get('value');

            // just hide if we don't have a value yet
            if (!field.has_value()) {
                return 'hide';
            }

            // first handle acceptance criteria logic
            // is of format: `acceptance_values: [values, ...]`
            if (event.acceptance_values) {
                if (_.contains(event.acceptance_values, field.get('acceptance_value'))) {
                    return 'show';
                }
                return 'hide';
            }

            // then handle legacy single value logic

            // Never allow dependent field for NA response on yesno
            if (field.get('type') === 'yesno' && field_val === NA) {
                return 'hide';
            }

            if (event.value == -1) {
                // 'any'
                return 'show';
            }

            // matching single value or multiple
            // Note: implicit boolean conversion here may have unexpected results but did not want to update
            // in case we are relying on this implicit conversion.
            if (field_val == event.value || ($.isArray(field_val) && _.contains(field_val, event.value))) {
                return 'show';
            }

            // finally handle legacy range logic for
            if (event.value instanceof Object && !isNaN(parseFloat(field_val))) {
                field_val = parseFloat(field_val);
                if (new FieldEventSearchCriterias(event.value.criterias).validate_value(field_val)) {
                    return 'show';
                }
            }

            return 'hide';
        }
    }
);

export const AttributeCriteriaFilter = BaseZenputModel.extend({
    defaults: {
        attribute_type: null,
        operator: null,
        value: null
    }
});

export const AttributeCriteriaFilters = BaseZenputCollection.extend({
    model: AttributeCriteriaFilter
});

export const AttributeCriteriaFilterSet = BaseZenputModel.extend({
    defaults: {
        boolean_operator: null,
        filters: null // AttributeCriteriaFilters
    },
    submodels: {
        filters: AttributeCriteriaFilters
    },
    _get_all_attributes_from_type: function (attribute_type, location_attributes) {
        return location_attributes.filter((attribute) => attribute.attribute_type === attribute_type);
    },
    _evaluate_filter: function (filter, location_attributes) {
        const attributes_from_type = this._get_all_attributes_from_type(
            filter.get('attribute_type'),
            location_attributes.get('attributes')
        );
        const operations = {
            EQ: (filter_value) => attributes_from_type.some((attribute) => _.isEqual(filter_value, attribute.value)),
            NOT_EQ: (filter_value) => !operations.EQ(filter_value),
            IN: (filter_values) => {
                if (!Array.isArray(filter_values)) {
                    throw new Error('IN value should be an array');
                }

                return filter_values.some(operations.EQ);
            }
        };
        return operations[filter.get('operator')](filter.get('value'));
    },
    evaluate: function (location_attributes) {
        const filters = this.get('filters');
        const boolean_operations = {
            AND: () => filters.every((filter) => this._evaluate_filter(filter, location_attributes)),
            OR: () => filters.some((filter) => this._evaluate_filter(filter, location_attributes))
        };

        return boolean_operations[this.get('boolean_operator')]();
    }
});

/**
 * Our title images should display PDFs last, since they are wider buttons than normal
 * image thumbnails. This is just a better orderd version of Images.
 *
 * @note Newer versions of Marionette have a `viewComparator` that would be better someday.
 */
export const TitleImagesAndPDFs = Images.extend({
    comparator: function (first, second) {
        var first_ext = $f.storage.get_file_extension(first.get('url'));
        var second_ext = $f.storage.get_file_extension(second.get('url'));

        if (first_ext === second_ext) {
            return 0;
        } // neutral
        if (first_ext === 'pdf') {
            return 1;
        } // first should be second
        if (second_ext === 'pdf') {
            return -1;
        } // second should be second
    }
});
